import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { toast } from "react-hot-toast";
import {
    fetchDesignRepArtworkProofMapping,
    resetArtworkProofing,
    saveDesignRepArtworkProofMappings,
    updateDesignRepArtworkProofMapping,
} from "../../services/orders";
import { getArtworkSelectionKey } from "../BinatedArtworkProofMapper/matching";
import FilesPopup from "../EditProduct/FilesPopup";
import { getCustomerFilesWithMeta, uploadFile } from "../../services/customerFiles";
import {
    APPROVAL_MODE_ORDER,
    COMPLETE_APPROVAL_MODE,
    PER_LOCATION_APPROVAL_MODE,
    deriveInitialApprovalMode,
} from "./approvalMode";

const APPLIES_TO_ORDER = "order";
const APPLIES_TO_PRODUCTS = "products";
const APPLIES_TO_DECORATIONS = "decorations";

const APPROVAL_MODE_OPTIONS_BY_VALUE = {
    [COMPLETE_APPROVAL_MODE]: {
        value: COMPLETE_APPROVAL_MODE,
        label: "Complete package",
        shortDescription: "Customer approves the proof set once",
        tooltip:
            "Use this when the proof files should be presented to the customer as one approval package. Files can still be assigned internally to specific products or decoration locations, but the customer receives one approve/request-changes decision for the complete package.",
        icon: FactCheckOutlinedIcon,
    },
    [PER_LOCATION_APPROVAL_MODE]: {
        value: PER_LOCATION_APPROVAL_MODE,
        label: "Per location",
        shortDescription: "Approve each artwork location separately",
        tooltip:
            "Use this when individual artwork placements need separate proof approval. Each product decoration/location is mapped to its own proof file and tracked independently. The customer can approve or request changes for each applicable proof rather than approving the entire Sales Order proof set as one package.",
        icon: PlaceOutlinedIcon,
    },
};

const APPROVAL_MODE_OPTIONS = APPROVAL_MODE_ORDER.map((mode) => APPROVAL_MODE_OPTIONS_BY_VALUE[mode]);

const buildFallbackSourceFileId = (row = {}) => {
    const explicitId = String(row?.sourceFileId || "").trim();
    if (explicitId) return explicitId;

    const path = String(row?.sourceFilePath || "").trim();
    if (path) return `design-rep:${path}`;

    const fileName = String(row?.sourceFileName || "").trim();
    if (fileName) return `design-rep:${fileName}`;

    return "";
};

const getDisplayFileName = (row = {}) => {
    const explicitName = String(row?.sourceFileName || "").trim();
    if (explicitName) return explicitName;

    const path = String(row?.sourceFilePath || "").trim();
    if (!path) return "No proof file selected yet";

    const sanitized = path.split("?")[0].split("#")[0];
    const lastSegment = sanitized.split("/").filter(Boolean).pop();
    return lastSegment || "Proof file";
};

const getDisplayFilePath = (row = {}) => String(row?.sourceFilePath || "").trim();
const IMAGE_FILE_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;

const getPreviewImagePath = (row = {}) => {
    const meta = row?.sourceFileMeta && typeof row.sourceFileMeta === "object" ? row.sourceFileMeta : {};
    const candidates = [
        meta?.previewUrl,
        meta?.thumbnailUrl,
        meta?.downloadUrl,
        meta?.url,
        row?.sourceFilePath,
    ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);

    return candidates.find((value) => IMAGE_FILE_RX.test(value)) || "";
};

const getArtworkLabel = (artwork = {}, index = 0) =>
    artwork?.logoName || artwork?.fileName || `Artwork ${index + 1}`;

const getArtworkSecondaryLabel = (artwork = {}) =>
    [
        artwork?.location ? `Location: ${artwork.location}` : null,
        artwork?.imprintType ? `Imprint: ${artwork.imprintType}` : null,
        artwork?.orderItemId ? `Order Item: ${artwork.orderItemId}` : null,
    ]
        .filter(Boolean)
        .join(" | ");

const buildRowState = (artwork = {}, mapping = null) => ({
    artworkId: artwork?.artworkId ?? null,
    orderItemId: artwork?.orderItemId ?? null,
    decorationId: artwork?.decorationId ?? null,
    sourceFileId: buildFallbackSourceFileId({
        sourceFileId: mapping?.sourceFileId,
        sourceFileName: mapping?.sourceFileName || artwork?.mappedProof?.sourceFileName,
        sourceFilePath: mapping?.sourceFilePath || artwork?.mappedProof?.sourceFilePath,
    }),
    sourceFileName: mapping?.sourceFileName || artwork?.mappedProof?.sourceFileName || "",
    sourceFilePath: mapping?.sourceFilePath || artwork?.mappedProof?.sourceFilePath || "",
    sourceFileMeta:
        mapping?.sourceFileMeta && typeof mapping.sourceFileMeta === "object"
            ? { ...mapping.sourceFileMeta }
            : null,
});

const createRowMap = (response = {}) => {
    const mappingByKey = new Map();
    (Array.isArray(response?.mappings) ? response.mappings : [])
        .filter((mapping) => String(mapping?.approvalMode || PER_LOCATION_APPROVAL_MODE) !== COMPLETE_APPROVAL_MODE)
        .forEach((mapping) => {
            const key = getArtworkSelectionKey(mapping);
            if (!mappingByKey.has(key)) {
                mappingByKey.set(key, mapping);
            }
        });
    return mappingByKey;
};

const normalizeStatus = (proofRow = null) => {
    if (!proofRow) {
        return { tone: "default", label: "Not sent" };
    }
    const userState = String(proofRow?.approvals?.user?.state || "pending").toLowerCase();
    const clientState = String(proofRow?.approvals?.client?.state || "pending").toLowerCase();
    const proofStatus = String(proofRow?.proofStatus || proofRow?.status || "").toLowerCase();
    if (proofStatus === "rejected" || userState === "rejected" || clientState === "rejected") {
        return { tone: "error", label: "Changes requested" };
    }
    if (proofStatus === "approved" || (userState === "approved" && clientState === "approved")) {
        return { tone: "success", label: "Approved" };
    }
    if (userState === "approved" && clientState === "pending") {
        return { tone: "info", label: "Pending client review" };
    }
    return { tone: "warning", label: "Pending review" };
};

const isProofRowApproved = (proofRow = null) => normalizeStatus(proofRow).tone === "success";
const isProofRowRejected = (proofRow = null) => normalizeStatus(proofRow).tone === "error";
const hasProofReviewProgress = (proofRow = null) => {
    if (!proofRow) return false;
    const userState = String(proofRow?.approvals?.user?.state || "pending").toLowerCase();
    const clientState = String(proofRow?.approvals?.client?.state || "pending").toLowerCase();
    const proofStatus = String(proofRow?.proofStatus || proofRow?.status || "").toLowerCase();
    return (
        userState !== "pending" ||
        clientState !== "pending" ||
        proofStatus === "approved" ||
        proofStatus === "rejected" ||
        proofRow?.customerActionLocked === true
    );
};

const emptyAppliesTo = () => ({
    scope: APPLIES_TO_PRODUCTS,
    orderItemIds: [],
    decorationIds: [],
    artworkIds: [],
    purchaseOrderIds: [],
    supplierIds: [],
    decoratorIds: [],
});

const normalizeIdArray = (values = []) =>
    [...new Set((Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0))];

const buildPackageEntryFromMapping = (mapping = {}) => ({
    mappingId: mapping?.id ?? null,
    approvalGroupId: mapping?.approvalGroupId || "",
    proofScope: mapping?.proofScope || "order",
    sourceFileId: buildFallbackSourceFileId(mapping),
    sourceFileName: mapping?.sourceFileName || "",
    sourceFilePath: mapping?.sourceFilePath || "",
    sourceFileMeta:
        mapping?.sourceFileMeta && typeof mapping.sourceFileMeta === "object"
            ? { ...mapping.sourceFileMeta }
            : null,
    appliesTo:
        mapping?.appliesTo && typeof mapping.appliesTo === "object"
            ? {
                ...emptyAppliesTo(),
                ...mapping.appliesTo,
                orderItemIds: normalizeIdArray(mapping?.appliesTo?.orderItemIds),
                decorationIds: normalizeIdArray(mapping?.appliesTo?.decorationIds),
                artworkIds: normalizeIdArray(mapping?.appliesTo?.artworkIds),
                purchaseOrderIds: normalizeIdArray(mapping?.appliesTo?.purchaseOrderIds),
                supplierIds: normalizeIdArray(mapping?.appliesTo?.supplierIds),
                decoratorIds: normalizeIdArray(mapping?.appliesTo?.decoratorIds),
            }
            : emptyAppliesTo(),
    proofRow: mapping?.proofRow || null,
});

const emptyPackageEntry = (approvalGroupId = "") => ({
    mappingId: null,
    approvalGroupId,
    proofScope: "selected_targets",
    sourceFileId: "",
    sourceFileName: "",
    sourceFilePath: "",
    sourceFileMeta: null,
    appliesTo: emptyAppliesTo(),
    proofRow: null,
});

const normalizePackageFilesFromResponse = (response = {}) => {
    const packageMappings = (Array.isArray(response?.mappings) ? response.mappings : []).filter(
        (mapping) => String(mapping?.approvalMode || PER_LOCATION_APPROVAL_MODE) === COMPLETE_APPROVAL_MODE
    );
    if (!packageMappings.length) {
        return {
            approvalGroupId: "",
            packageFiles: [],
        };
    }

    const sorted = [...packageMappings].sort((left, right) => Number(left?.id || 0) - Number(right?.id || 0));
    return {
        approvalGroupId: sorted[0]?.approvalGroupId || "",
        packageFiles: sorted.map((mapping) => buildPackageEntryFromMapping(mapping)),
    };
};

const packageEntryComplete = (entry = {}) =>
    Boolean(
        buildFallbackSourceFileId(entry) &&
        String(entry?.sourceFileName || "").trim() &&
        String(entry?.sourceFilePath || "").trim()
    );

const buildPackagePayloadEntry = (entry = {}) => {
    const scope = entry?.appliesTo?.scope || APPLIES_TO_PRODUCTS;
    const orderItemIds = normalizeIdArray(entry?.appliesTo?.orderItemIds);
    const decorationIds = normalizeIdArray(entry?.appliesTo?.decorationIds);
    const artworkIds = normalizeIdArray(entry?.appliesTo?.artworkIds);
    return {
        sourceFileId: buildFallbackSourceFileId(entry),
        sourceFileName: String(entry?.sourceFileName || "").trim(),
        sourceFilePath: String(entry?.sourceFilePath || "").trim(),
        sourceFileMeta:
            entry?.sourceFileMeta && typeof entry.sourceFileMeta === "object" ? entry.sourceFileMeta : undefined,
        proofScope: scope === APPLIES_TO_ORDER ? "order" : "selected_targets",
        // Preserve a concrete target identity when this package file is assigned
        // to one artwork location. The appliesTo arrays remain authoritative for
        // multi-target package files, while these IDs keep document-side artwork
        // matching compatible with the existing per-location display path.
        artworkId: artworkIds.length === 1 ? artworkIds[0] : undefined,
        orderItemId: orderItemIds.length === 1 ? orderItemIds[0] : undefined,
        decorationId: decorationIds.length === 1 ? decorationIds[0] : undefined,
        appliesTo: {
            scope,
            orderItemIds,
            decorationIds,
            artworkIds,
            purchaseOrderIds: [],
            supplierIds: [],
            decoratorIds: [],
        },
    };
};

const getSelectedInternalFileUrl = (file = {}) => String(file?.filePath || "").trim();
const getSelectedInternalFileName = (file = {}) => String(file?.fileName || "").trim();

const getProductName = (artwork = {}) =>
    String(artwork?.product?.name || artwork?.productName || "").trim() ||
    (artwork?.orderItemId ? `Product #${artwork.orderItemId}` : "Product");

const getProductSku = (artwork = {}) => String(artwork?.product?.sku || artwork?.productSku || "").trim();

const getProductImage = (artwork = {}) =>
    String(artwork?.product?.image || artwork?.productImage || artwork?.originalImage || "").trim();

const getLocationLabel = (artwork = {}) =>
    String(artwork?.location || artwork?.decoration?.location || "").trim() || "Unknown location";

const getArtworkNameLabel = (artwork = {}) =>
    String(artwork?.logoName || artwork?.artwork?.logoName || "").trim() || "Artwork target";

const buildProductOptionLabel = (option = {}) => {
    const suffix = option?.targetCount === 1 ? "1 decoration location" : `${option?.targetCount || 0} decoration locations`;
    const parts = [option?.name || `Product #${option?.orderItemId}`];
    if (option?.sku) {
        parts.push(`SKU: ${option.sku}`);
    }
    parts.push(suffix);
    return parts.join(" — ");
};

const buildDecorationOptionLabel = (option = {}) => {
    const parts = [
        option?.productName || `Product #${option?.orderItemId || "?"}`,
        option?.location || "Unknown location",
        option?.artworkName || "Artwork target",
    ].filter(Boolean);
    return parts.join(" — ");
};

const isAggregatedPortalProofSource = (sourceType = "") =>
    ["core-network", "internal"].includes(String(sourceType || "").trim().toLowerCase());

const getWorkRequestGroupLabel = (group = {}) => {
    const requestId = group?.workRequestId ? `Request #${group.workRequestId}` : "Request";
    const version = group?.submissionVersion ? `v${group.submissionVersion}` : null;
    return [requestId, version].filter(Boolean).join(" â€¢ ");
};

const buildProductOptions = (artworks = []) => {
    const byOrderItemId = new Map();
    (Array.isArray(artworks) ? artworks : []).forEach((artwork) => {
        const orderItemId = Number(artwork?.orderItemId);
        if (!Number.isInteger(orderItemId) || orderItemId <= 0) return;
        const targetKey = [
            String(artwork?.decorationId ?? ""),
            String(artwork?.artworkId ?? ""),
        ].join("::");
        if (!byOrderItemId.has(orderItemId)) {
            byOrderItemId.set(orderItemId, {
                orderItemId,
                name: getProductName(artwork),
                sku: getProductSku(artwork),
                image: getProductImage(artwork),
                targetKeys: new Set(),
            });
        }
        byOrderItemId.get(orderItemId).targetKeys.add(targetKey);
    });

    return [...byOrderItemId.values()]
        .map((entry) => ({
            ...entry,
            targetCount: entry.targetKeys.size,
        }))
        .sort((left, right) => buildProductOptionLabel(left).localeCompare(buildProductOptionLabel(right)));
};

const buildDecorationOptions = (artworks = []) => {
    const byKey = new Map();
    (Array.isArray(artworks) ? artworks : []).forEach((artwork) => {
        const decorationId = Number(artwork?.decorationId);
        if (!Number.isInteger(decorationId) || decorationId <= 0) return;
        const orderItemId = Number(artwork?.orderItemId);
        const artworkId = Number(artwork?.artworkId);
        const key = [String(orderItemId || ""), String(decorationId), String(artworkId || "")].join("::");
        if (byKey.has(key)) return;
        byKey.set(key, {
            key,
            orderItemId: Number.isInteger(orderItemId) && orderItemId > 0 ? orderItemId : null,
            decorationId,
            artworkId: Number.isInteger(artworkId) && artworkId > 0 ? artworkId : null,
            productName: getProductName(artwork),
            location: getLocationLabel(artwork),
            imprintType: String(artwork?.imprintType || artwork?.decoration?.imprintType || "").trim(),
            artworkName: getArtworkNameLabel(artwork),
            image: getProductImage(artwork),
        });
    });

    return [...byKey.values()].sort((left, right) => buildDecorationOptionLabel(left).localeCompare(buildDecorationOptionLabel(right)));
};

const createScopeSelectionEntry = (scope = APPLIES_TO_PRODUCTS) => {
    if (scope === APPLIES_TO_ORDER) {
        return {
            proofScope: "order",
            appliesTo: {
                ...emptyAppliesTo(),
                scope: APPLIES_TO_ORDER,
            },
        };
    }
    if (scope === APPLIES_TO_PRODUCTS) {
        return {
            proofScope: "selected_targets",
            appliesTo: {
                ...emptyAppliesTo(),
                scope: APPLIES_TO_PRODUCTS,
            },
        };
    }
    if (scope === APPLIES_TO_DECORATIONS) {
        return {
            proofScope: "selected_targets",
            appliesTo: {
                ...emptyAppliesTo(),
                scope: APPLIES_TO_DECORATIONS,
            },
        };
    }
    return {
        proofScope: "selected_targets",
        appliesTo: {
            ...emptyAppliesTo(),
            scope: APPLIES_TO_PRODUCTS,
        },
    };
};

const isLegacyOrderTargeting = (entry = {}) =>
    String(entry?.proofScope || "").trim().toLowerCase() === "order" ||
    String(entry?.appliesTo?.scope || "").trim().toLowerCase() === APPLIES_TO_ORDER;

const validatePackageEntryTargets = (entry = {}) => {
    const scope = String(entry?.appliesTo?.scope || "").trim().toLowerCase();
    if (scope === APPLIES_TO_ORDER) {
        return "";
    }
    if (scope === APPLIES_TO_PRODUCTS) {
        if (!normalizeIdArray(entry?.appliesTo?.orderItemIds).length) {
            return "Select at least one product this proof package applies to.";
        }
        return "";
    }

    if (scope === APPLIES_TO_DECORATIONS) {
        const decorationIds = normalizeIdArray(entry?.appliesTo?.decorationIds);
        const artworkIds = normalizeIdArray(entry?.appliesTo?.artworkIds);
        if (!decorationIds.length && !artworkIds.length) {
            return "Select at least one decoration/location this proof package applies to.";
        }
        return "";
    }

    return "Choose product or decoration/location targeting for this proof package file before saving.";
};

const isLegacyPoGroupTargeting = (entry = {}) =>
    String(entry?.proofScope || "").trim().toLowerCase() === "po_group" ||
    String(entry?.appliesTo?.scope || "").trim().toLowerCase() === "po_group" ||
    normalizeIdArray(entry?.appliesTo?.purchaseOrderIds).length > 0 ||
    normalizeIdArray(entry?.appliesTo?.supplierIds).length > 0 ||
    normalizeIdArray(entry?.appliesTo?.decoratorIds).length > 0;

const resolveSelectedProductOptions = (entry = {}, productOptions = []) => {
    const selectedIds = new Set(normalizeIdArray(entry?.appliesTo?.orderItemIds).map(String));
    return productOptions.filter((option) => selectedIds.has(String(option.orderItemId)));
};

const countMissingProductTargets = (entry = {}, productOptions = []) => {
    const available = new Set(productOptions.map((option) => String(option.orderItemId)));
    return normalizeIdArray(entry?.appliesTo?.orderItemIds).filter((id) => !available.has(String(id))).length;
};

const resolveSelectedDecorationOptions = (entry = {}, decorationOptions = []) => {
    const selectedDecorationIds = new Set(normalizeIdArray(entry?.appliesTo?.decorationIds).map(String));
    const selectedArtworkIds = new Set(normalizeIdArray(entry?.appliesTo?.artworkIds).map(String));
    return decorationOptions.filter((option) => {
        const decorationMatch = selectedDecorationIds.has(String(option.decorationId));
        if (!decorationMatch) return false;
        if (selectedArtworkIds.size === 0) return true;
        if (option.artworkId == null) return false;
        return selectedArtworkIds.has(String(option.artworkId));
    });
};

const countMissingDecorationTargets = (entry = {}, decorationOptions = []) => {
    const matchedOptions = resolveSelectedDecorationOptions(entry, decorationOptions);
    const matchedKeys = new Set(matchedOptions.map((option) => option.key));
    const selectedDecorationIds = normalizeIdArray(entry?.appliesTo?.decorationIds);
    const selectedArtworkIds = normalizeIdArray(entry?.appliesTo?.artworkIds);
    if (!selectedDecorationIds.length) return 0;

    const expectedKeys = new Set();
    if (selectedArtworkIds.length > 0) {
        selectedDecorationIds.forEach((decorationId) => {
            selectedArtworkIds.forEach((artworkId) => {
                const option = decorationOptions.find(
                    (candidate) => candidate.decorationId === decorationId && candidate.artworkId === artworkId
                );
                expectedKeys.add(option ? option.key : `${decorationId}::${artworkId}`);
            });
        });
    } else {
        selectedDecorationIds.forEach((decorationId) => {
            const options = decorationOptions.filter((candidate) => candidate.decorationId === decorationId);
            if (!options.length) {
                expectedKeys.add(`${decorationId}::missing`);
                return;
            }
            options.forEach((option) => expectedKeys.add(option.key));
        });
    }

    let missing = 0;
    expectedKeys.forEach((key) => {
        if (!matchedKeys.has(key)) {
            missing += 1;
        }
    });
    return missing;
};

export default function DesignRepArtworkProofMapper({
    active = false,
    orderId,
    documentType,
    documentId,
    customerId = null,
    disabled = false,
    sourceType = "design_rep",
    sourceLabel = "Design Rep",
    sourceProvider = null,
    salesOrderWorkflowStatus = "",
    onMappingsSaved,
    onSourceSummaryChange,
    onResetComplete,
    resetBlockedReason = "",
}) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [mapperData, setMapperData] = useState(null);
    const [mode, setMode] = useState(COMPLETE_APPROVAL_MODE);
    const [rowsByKey, setRowsByKey] = useState({});
    const [customerFiles, setCustomerFiles] = useState([]);
    const [customerFilesLoading, setCustomerFilesLoading] = useState(false);
    const [filePickerTarget, setFilePickerTarget] = useState(null);
    const [revisionUnlocked, setRevisionUnlocked] = useState(false);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [resetDialogState, setResetDialogState] = useState(null);
    const [resetting, setResetting] = useState(false);
    const [packageState, setPackageState] = useState({
        approvalGroupId: "",
        packageFiles: [],
    });

    const trimmedOrderId = String(orderId || "").trim();
    const trimmedDocumentType = String(documentType || "").trim();
    const trimmedDocumentId = String(documentId || "").trim();
    const trimmedCustomerId = String(customerId || "").trim();
    const trimmedSourceType = String(sourceType || "design_rep").trim() || "design_rep";
    const isProofingWorkflow = String(salesOrderWorkflowStatus || "").trim().toLowerCase() === "proofing";
    const trimmedSourceProvider = String(sourceProvider || trimmedSourceType).trim() || trimmedSourceType;

    const missingContextMessage = useMemo(() => {
        const missing = [];
        if (!trimmedOrderId) missing.push("orderId");
        if (!trimmedDocumentType) missing.push("documentType");
        if (!trimmedDocumentId) missing.push("documentId");
        return missing.length ? `${sourceLabel} proofing requires ${missing.join(", ")} before it can load.` : "";
    }, [sourceLabel, trimmedDocumentId, trimmedDocumentType, trimmedOrderId]);

    const hasRequiredContext = !missingContextMessage;
    const activeMappings = useMemo(
        () => (Array.isArray(mapperData?.mappings) ? mapperData.mappings : []).filter((mapping) => mapping?.isActive !== false),
        [mapperData]
    );
    const hasMeaningfulResetState = activeMappings.length > 0 || isProofingWorkflow;
    const hasPersistedCompletePackage = activeMappings.some(
        (mapping) => String(mapping?.approvalMode || PER_LOCATION_APPROVAL_MODE) === COMPLETE_APPROVAL_MODE
    );
    const localResetBlockedReason = useMemo(() => {
        if (resetBlockedReason) return resetBlockedReason;
        if (activeMappings.some((mapping) => hasProofReviewProgress(mapping?.proofRow || null))) {
            return "Artwork & Proofing cannot be reset after proof review has started or a customer approval decision has been recorded.";
        }
        return "";
    }, [activeMappings, resetBlockedReason]);

    const hydrateRowsFromResponse = useCallback((response) => {
        const mappingByKey = createRowMap(response);
        const nextRows = {};
        (Array.isArray(response?.artworks) ? response.artworks : []).forEach((artwork) => {
            const key = getArtworkSelectionKey(artwork);
            nextRows[key] = buildRowState(artwork, mappingByKey.get(key) || null);
        });
        setRowsByKey(nextRows);
    }, []);

    const hydratePackageFromResponse = useCallback((response) => {
        setPackageState(normalizePackageFilesFromResponse(response));
    }, []);

    const loadMapperData = useCallback(async () => {
        if (!hasRequiredContext) return null;
        try {
            setLoading(true);
            setLoadError("");
            const response = await fetchDesignRepArtworkProofMapping(trimmedDocumentType, trimmedDocumentId, {
                orderId: trimmedOrderId,
                sourceType: trimmedSourceType,
            });
            setMapperData(response || null);
            hydrateRowsFromResponse(response || {});
            hydratePackageFromResponse(response || {});
            setMode(deriveInitialApprovalMode(response || {}));
            setRevisionUnlocked(false);
            setReopenDialogOpen(false);
            return response || null;
        } catch (error) {
            setLoadError(error?.response?.data?.message || `Failed to load ${sourceLabel} proof mapping.`);
            return null;
        } finally {
            setLoading(false);
        }
    }, [hasRequiredContext, hydratePackageFromResponse, hydrateRowsFromResponse, sourceLabel, trimmedDocumentId, trimmedDocumentType, trimmedOrderId, trimmedSourceType]);

    useEffect(() => {
        if (!active) return;
        loadMapperData();
    }, [active, loadMapperData]);

    const loadCustomerFiles = useCallback(async () => {
        if (!active || !trimmedCustomerId) {
            setCustomerFiles([]);
            setCustomerFilesLoading(false);
            return [];
        }

        try {
            setCustomerFilesLoading(true);
            const files = await getCustomerFilesWithMeta({ customerId: trimmedCustomerId });
            const normalizedFiles = Array.isArray(files) ? files : [];
            setCustomerFiles(normalizedFiles);
            return normalizedFiles;
        } catch (_) {
            setCustomerFiles([]);
            return [];
        } finally {
            setCustomerFilesLoading(false);
        }
    }, [active, trimmedCustomerId]);

    useEffect(() => {
        let cancelled = false;

        const syncCustomerFiles = async () => {
            if (!active || !trimmedCustomerId) {
                setCustomerFiles([]);
                setCustomerFilesLoading(false);
                return;
            }

            try {
                setCustomerFilesLoading(true);
                const files = await getCustomerFilesWithMeta({ customerId: trimmedCustomerId });
                if (!cancelled) {
                    setCustomerFiles(Array.isArray(files) ? files : []);
                }
            } catch (_) {
                if (!cancelled) {
                    setCustomerFiles([]);
                }
            } finally {
                if (!cancelled) {
                    setCustomerFilesLoading(false);
                }
            }
        };

        syncCustomerFiles();
        return () => {
            cancelled = true;
        };
    }, [active, trimmedCustomerId]);

    const handleCustomerFileUpload = useCallback(async (targetCustomerId, payload) => {
        const uploadCustomerId = String(targetCustomerId || trimmedCustomerId || "").trim();
        if (!uploadCustomerId) {
            throw new Error("A customer must be selected before uploading internal files.");
        }

        const uploadedFile = await uploadFile(uploadCustomerId, payload);
        await loadCustomerFiles();
        return uploadedFile;
    }, [loadCustomerFiles, trimmedCustomerId]);

    const artworks = Array.isArray(mapperData?.artworks) ? mapperData.artworks : [];
    const rowMappingMap = useMemo(() => createRowMap(mapperData || {}), [mapperData]);
    const packageMappingById = useMemo(() => {
        const next = new Map();
        (Array.isArray(mapperData?.mappings) ? mapperData.mappings : [])
            .filter((mapping) => String(mapping?.approvalMode || PER_LOCATION_APPROVAL_MODE) === COMPLETE_APPROVAL_MODE)
            .forEach((mapping) => {
                if (mapping?.id != null) {
                    next.set(String(mapping.id), buildPackageEntryFromMapping(mapping));
                }
            });
        return next;
    }, [mapperData]);
    const rows = useMemo(
        () =>
            artworks.map((artwork) => {
                const key = getArtworkSelectionKey(artwork);
                return {
                    key,
                    artwork,
                    values: rowsByKey[key] || buildRowState(artwork),
                };
            }),
        [artworks, rowsByKey]
    );

    const productOptions = useMemo(() => buildProductOptions(artworks), [artworks]);
    const decorationOptions = useMemo(() => buildDecorationOptions(artworks), [artworks]);
    const packageFiles = packageState.packageFiles || [];
    const sourceSyncSummary = useMemo(
        () => (mapperData?.sourceSyncSummary && typeof mapperData.sourceSyncSummary === "object" ? mapperData.sourceSyncSummary : null),
        [mapperData]
    );
    const aggregatedSourcePrimaryGroup = useMemo(
        () => (Array.isArray(sourceSyncSummary?.fileGroups) && sourceSyncSummary.fileGroups.length ? sourceSyncSummary.fileGroups[0] : null),
        [sourceSyncSummary]
    );
    const perLocationSummary = useMemo(() => {
        const total = rows.length;
        let approved = 0;
        let rejected = 0;
        let pending = 0;

        rows.forEach((entry) => {
            const mapping = rowMappingMap.get(entry.key) || null;
            const proofRow = mapping?.proofRow || null;
            const hasMappedProof = packageEntryComplete(entry.values);
            const baseline = buildRowState(entry.artwork, mapping);
            const locallyChanged = JSON.stringify(entry.values) !== JSON.stringify(baseline);

            if (!hasMappedProof || !proofRow || locallyChanged) {
                pending += 1;
                return;
            }

            if (isProofRowRejected(proofRow)) {
                rejected += 1;
                return;
            }

            if (isProofRowApproved(proofRow)) {
                approved += 1;
                return;
            }

            pending += 1;
        });

        return { total, approved, rejected, pending };
    }, [rowMappingMap, rows]);
    const completePackageSummary = useMemo(() => {
        const total = packageFiles.length;
        let approved = 0;
        let rejected = 0;
        let pending = 0;

        packageFiles.forEach((entry) => {
            const hasValidFile = packageEntryComplete(entry);
            const proofRow = entry?.proofRow || null;
            const baseline = entry?.mappingId != null ? packageMappingById.get(String(entry.mappingId)) || null : null;
            const locallyChanged = entry?.mappingId == null
                ? hasValidFile
                : baseline
                    ? JSON.stringify(entry) !== JSON.stringify(baseline)
                    : hasValidFile;

            if (!hasValidFile || !proofRow || locallyChanged) {
                pending += 1;
                return;
            }

            if (isProofRowRejected(proofRow)) {
                rejected += 1;
                return;
            }

            if (isProofRowApproved(proofRow)) {
                approved += 1;
                return;
            }

            pending += 1;
        });

        return { total, approved, rejected, pending };
    }, [packageFiles, packageMappingById]);
    const activeProofSummary = mode === COMPLETE_APPROVAL_MODE ? completePackageSummary : perLocationSummary;
    const proofingComplete =
        activeProofSummary.total > 0 &&
        activeProofSummary.approved === activeProofSummary.total &&
        activeProofSummary.pending === 0 &&
        activeProofSummary.rejected === 0;
    const hasRejectedProofs = activeProofSummary.rejected > 0;
    const hasPendingProofs = activeProofSummary.pending > 0;
    useEffect(() => {
        if (typeof onSourceSummaryChange !== "function") return;
        onSourceSummaryChange({
            sourceType: trimmedSourceType,
            sourceSyncSummary,
            primaryGroup: aggregatedSourcePrimaryGroup,
            proofingComplete,
            hasPendingProofs,
            hasRejectedProofs,
        });
    }, [
        aggregatedSourcePrimaryGroup,
        hasPendingProofs,
        hasRejectedProofs,
        onSourceSummaryChange,
        proofingComplete,
        sourceSyncSummary,
        trimmedSourceType,
    ]);
    const editingLocked = proofingComplete && !revisionUnlocked;
    const interactionDisabled = disabled || loading || saving || editingLocked;
    const resetActionDisabled = Boolean(localResetBlockedReason) || interactionDisabled || resetting;
    const handleSelectInternalFile = useCallback((file) => {
        const fileUrl = getSelectedInternalFileUrl(file);
        if (!fileUrl || !filePickerTarget) {
            toast.error("Selected file does not have a usable URL.");
            return;
        }
        const fileName = getSelectedInternalFileName(file);

        if (filePickerTarget.type === "row") {
            setRowsByKey((prev) => {
                const current = prev[filePickerTarget.key] || {};
                const next = {
                    ...current,
                    sourceFilePath: fileUrl,
                    sourceFileName: fileName || current.sourceFileName || "",
                    sourceFileMeta: file,
                };
                next.sourceFileId = buildFallbackSourceFileId(next);
                return {
                    ...prev,
                    [filePickerTarget.key]: next,
                };
            });
        }

        if (filePickerTarget.type === "package") {
            setPackageState((prev) => {
                const nextFiles = [...prev.packageFiles];
                const current = nextFiles[filePickerTarget.index] || emptyPackageEntry(prev.approvalGroupId);
                const next = {
                    ...current,
                    sourceFilePath: fileUrl,
                    sourceFileName: fileName || current.sourceFileName || "",
                    sourceFileMeta: file,
                };
                next.sourceFileId = buildFallbackSourceFileId(next);
                nextFiles[filePickerTarget.index] = next;
                return {
                    ...prev,
                    packageFiles: nextFiles,
                };
            });
        }

        setFilePickerTarget(null);
    }, [filePickerTarget]);

    const handleSavePerLocation = useCallback(async () => {
        const dirtyRows = rows.filter((entry) => {
            const baseline = buildRowState(entry.artwork, createRowMap(mapperData).get(entry.key) || null);
            return JSON.stringify(entry.values) !== JSON.stringify(baseline);
        });

        if (!dirtyRows.length) {
            toast("No mapping changes to save.");
            return;
        }

        const incomplete = dirtyRows.find((entry) => !packageEntryComplete(entry.values));
        if (incomplete) {
            toast.error("Each changed row needs a proof file name and URL/path.");
            return;
        }

        try {
            setSaving(true);
            const response = await saveDesignRepArtworkProofMappings(trimmedDocumentType, trimmedDocumentId, {
                orderId: trimmedOrderId,
                sourceType: trimmedSourceType,
                sourceProvider: trimmedSourceProvider,
                approvalMode: PER_LOCATION_APPROVAL_MODE,
                mappings: dirtyRows.map((entry) => ({
                    artworkId: entry.values.artworkId,
                    orderItemId: entry.values.orderItemId,
                    decorationId: entry.values.decorationId,
                    sourceFileId: buildFallbackSourceFileId(entry.values),
                    sourceFileName: String(entry.values.sourceFileName || "").trim(),
                    sourceFilePath: String(entry.values.sourceFilePath || "").trim(),
                    sourceFileMeta: entry.values.sourceFileMeta || undefined,
                })),
            });
            setMapperData(response || null);
            hydrateRowsFromResponse(response || {});
            hydratePackageFromResponse(response || {});
            setRevisionUnlocked(false);
            onMappingsSaved?.(response || null);
            toast.success(`${sourceLabel} proof mappings saved.`);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save proof mappings.");
        } finally {
            setSaving(false);
        }
    }, [hydratePackageFromResponse, hydrateRowsFromResponse, mapperData, onMappingsSaved, rows, sourceLabel, trimmedDocumentId, trimmedDocumentType, trimmedOrderId, trimmedSourceProvider, trimmedSourceType]);

    const clearPersistedProofSetupLocally = useCallback(() => {
        const nextResponse = {
            ...(mapperData || {}),
            mappings: [],
            packages: [],
            activeApprovalMode: null,
        };
        setMapperData(nextResponse);
        hydrateRowsFromResponse({
            artworks: Array.isArray(mapperData?.artworks) ? mapperData.artworks : [],
            mappings: [],
        });
        hydratePackageFromResponse({ mappings: [] });
        setMode(COMPLETE_APPROVAL_MODE);
        setRevisionUnlocked(false);
        setReopenDialogOpen(false);
    }, [hydratePackageFromResponse, hydrateRowsFromResponse, mapperData]);

    const handleResetArtworkProofing = useCallback(async () => {
        if (!hasMeaningfulResetState || resetActionDisabled) return;

        try {
            setResetting(true);
            await resetArtworkProofing(trimmedOrderId, trimmedDocumentId);
            clearPersistedProofSetupLocally();
            setResetDialogState(null);
            onMappingsSaved?.(null);
            await onResetComplete?.();
            toast.success("Artwork & Proofing reset.");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to reset Artwork & Proofing.");
        } finally {
            setResetting(false);
        }
    }, [clearPersistedProofSetupLocally, hasMeaningfulResetState, onMappingsSaved, onResetComplete, resetActionDisabled, trimmedDocumentId, trimmedOrderId]);

    const handleSavePackage = useCallback(async () => {
        if (!packageFiles.length) {
            if (hasPersistedCompletePackage) {
                if (localResetBlockedReason) {
                    toast.error(localResetBlockedReason);
                    return;
                }
                setResetDialogState({
                    title: "Remove all proofs and reset Artwork & Proofing?",
                    description:
                        "Removing every proof file will reset Artwork & Proofing to its default state. Existing artwork, proof files, and proof history will be preserved.",
                });
                return;
            }
            toast.error("Add at least one proof file to the package.");
            return;
        }
        const incomplete = packageFiles.find((entry) => !packageEntryComplete(entry));
        if (incomplete) {
            toast.error("Each package file needs a proof file name and URL/path.");
            return;
        }
        const invalidTargetMessage = packageFiles
            .map((entry) => validatePackageEntryTargets(entry))
            .find(Boolean);
        if (invalidTargetMessage) {
            toast.error(invalidTargetMessage);
            return;
        }

        try {
            setSaving(true);
            const response = await saveDesignRepArtworkProofMappings(trimmedDocumentType, trimmedDocumentId, {
                orderId: trimmedOrderId,
                sourceType: trimmedSourceType,
                sourceProvider: trimmedSourceProvider,
                approvalMode: COMPLETE_APPROVAL_MODE,
                approvalGroupId: packageState.approvalGroupId || undefined,
                proofScope: "selected_targets",
                packageFiles: packageFiles.map((entry) => buildPackagePayloadEntry(entry)),
            });
            setMapperData(response || null);
            hydrateRowsFromResponse(response || {});
            hydratePackageFromResponse(response || {});
            setMode(COMPLETE_APPROVAL_MODE);
            setRevisionUnlocked(false);
            onMappingsSaved?.(response || null);
            toast.success(`${sourceLabel} proof package saved.`);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save the complete approval package.");
        } finally {
            setSaving(false);
        }
    }, [hasPersistedCompletePackage, hydratePackageFromResponse, hydrateRowsFromResponse, localResetBlockedReason, onMappingsSaved, packageFiles, packageState.approvalGroupId, sourceLabel, trimmedDocumentId, trimmedDocumentType, trimmedOrderId, trimmedSourceProvider, trimmedSourceType]);

    const handleUpdatePackageMapping = useCallback(async (entry) => {
        if (!entry?.mappingId) return;
        const invalidTargetMessage = validatePackageEntryTargets(entry);
        if (invalidTargetMessage) {
            toast.error(invalidTargetMessage);
            return;
        }
        try {
            await updateDesignRepArtworkProofMapping(entry.mappingId, buildPackagePayloadEntry(entry));
            setRevisionUnlocked(false);
            toast.success("Package assignment updated.");
            await loadMapperData();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update the package file assignment.");
        }
    }, [loadMapperData]);

    const updatePackageEntry = useCallback((index, updater) => {
        setPackageState((prev) => {
            const nextFiles = [...prev.packageFiles];
            const current = nextFiles[index] || emptyPackageEntry(prev.approvalGroupId);
            const next = typeof updater === "function" ? updater(current) : { ...current, ...updater };
            nextFiles[index] = next;
            return {
                ...prev,
                packageFiles: nextFiles,
            };
        });
    }, []);

    const customerFileGroups = { "Client Files": customerFiles };
    const selectedFilePath = (() => {
        if (!filePickerTarget) return "";
        if (filePickerTarget.type === "row") {
            return rowsByKey[filePickerTarget.key]?.sourceFilePath || "";
        }
        if (filePickerTarget.type === "package") {
            return packageFiles[filePickerTarget.index]?.sourceFilePath || "";
        }
        return "";
    })();

    useEffect(() => {
        if (!editingLocked) return;
        setFilePickerTarget(null);
    }, [editingLocked]);

    if (!active) return null;

    return (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {(loading || saving) ? <LinearProgress /> : null}

            {loadError ? <Alert severity="error">{loadError}</Alert> : null}
            {missingContextMessage ? <Alert severity="warning">{missingContextMessage}</Alert> : null}
            {proofingComplete ? (
                <Alert
                    severity="success"
                    action={
                        !revisionUnlocked ? (
                            <Button color="inherit" size="small" onClick={() => setReopenDialogOpen(true)}>
                                Reopen proofing for revision
                            </Button>
                        ) : null
                    }
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        All artwork proofs are approved
                    </Typography>
                    <Typography variant="body2">
                        The customer has approved all proof files for this Sales Order. You can continue with the Sales Order approval workflow.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Need to make a change? Reopen proofing for revision. Any revised proof will need to be approved again before the Sales Order can proceed.
                    </Typography>
                    {revisionUnlocked ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Proofing has been reopened for revision. Any proof you revise will need customer approval again before the Sales Order can proceed.
                        </Typography>
                    ) : null}
                </Alert>
            ) : null}
            {trimmedSourceType !== "core-network" && !proofingComplete && (hasPendingProofs || hasRejectedProofs) ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {hasRejectedProofs
                        ? "At least one proof has changes requested or is no longer approved."
                        : "Artwork proofing is still awaiting customer approval."}
                </Typography>
            ) : null}
            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 2,
                    alignItems: "start",
                }}
            >
                <Stack spacing={2} sx={{ width: "100%" }}>
                    <Card variant="outlined">
                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box>
                                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                                    Step 1
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    How will the customer approve?
                                </Typography>
                            </Box>
                            <Box
                                role="radiogroup"
                                aria-label="Approval Mode"
                                sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}
                            >
                                {APPROVAL_MODE_OPTIONS.map((option) => {
                                    const OptionIcon = option.icon;
                                    const selected = mode === option.value;
                                    return (
                                        <Tooltip key={option.value} title={option.tooltip} arrow placement="top">
                                            <Box
                                                component="button"
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                aria-label={`${option.label}: ${option.shortDescription}`}
                                                onClick={() => setMode(option.value)}
                                                disabled={interactionDisabled}
                                                sx={{
                                                    width: "100%",
                                                    textAlign: "left",
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1.25,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: "1px solid",
                                                    borderColor: selected ? "primary.main" : "divider",
                                                    backgroundColor: selected ? "var(--ec-color-primary-soft)" : "background.paper",
                                                    boxShadow: selected ? "0 0 0 1px var(--ec-color-primary)" : "none",
                                                    color: "text.primary",
                                                    cursor: interactionDisabled ? "not-allowed" : "pointer",
                                                    opacity: interactionDisabled ? 0.6 : 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        mt: 0.15,
                                                        borderRadius: "50%",
                                                        border: "2px solid",
                                                        borderColor: selected ? "primary.main" : "divider",
                                                        backgroundColor: "#fff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {selected ? (
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                backgroundColor: "primary.main",
                                                            }}
                                                        />
                                                    ) : null}
                                                </Box>
                                                <OptionIcon sx={{ fontSize: 18, mt: 0.05, color: selected ? "primary.main" : "text.secondary", flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                        {option.label}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.35 }}>
                                                        {option.shortDescription}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>

                    {Array.isArray(mapperData?.warnings) && mapperData.warnings.length > 0 ? (
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: "1px solid var(--ec-status-warning-border)",
                                backgroundColor: "var(--ec-status-warning-bg)",
                                px: 1.5,
                                py: 1.25,
                            }}
                        >
                            {mapperData.warnings.map((warning, index) => (
                                <Typography key={`${warning?.code || "warning"}-${index}`} variant="body2" sx={{ color: "var(--ec-status-warning-text)" }}>
                                    {warning?.message || "Warning"}
                                </Typography>
                            ))}
                        </Box>
                    ) : null}

                    <Card variant="outlined">
                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: "text.secondary" }}>
                                        Step 2
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Add proof files
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {mode === COMPLETE_APPROVAL_MODE
                                            ? "Build the proof package the customer will review."
                                            : "Assign the proof file for each artwork location."}
                                    </Typography>
                                    {mode === PER_LOCATION_APPROVAL_MODE ? (
                                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                                            Each proof is automatically linked to this product and decoration/location.
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {mode === COMPLETE_APPROVAL_MODE ? (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() =>
                                                setPackageState((prev) => ({
                                                    ...prev,
                                                    packageFiles: [...prev.packageFiles, emptyPackageEntry(prev.approvalGroupId)],
                                                }))
                                            }
                                            disabled={interactionDisabled}
                                        >
                                            Add proof file
                                        </Button>
                                    ) : null}
                                    {hasMeaningfulResetState ? (
                                        <Tooltip
                                            title={
                                                localResetBlockedReason ||
                                                "Clear the current proof setup and return Artwork & Proofing to its default state."
                                            }
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    color="error"
                                                    variant="text"
                                                    onClick={() =>
                                                        setResetDialogState({
                                                            title: "Reset Artwork & Proofing?",
                                                            description:
                                                                "This will clear the current proof setup and return Artwork & Proofing to its default state. Existing artwork, proof files, and proof history will be preserved.",
                                                        })
                                                    }
                                                    disabled={resetActionDisabled}
                                                >
                                                    Reset Artwork & Proofing
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </Box>
                            </Box>

                            {mode === PER_LOCATION_APPROVAL_MODE ? (
                                <>
                                    {!loading && !rows.length ? (
                                        <Box
                                            sx={{
                                                borderRadius: 2,
                                                border: "1px solid var(--ec-color-border)",
                                                backgroundColor: "var(--ec-color-surface-muted)",
                                                px: 1.5,
                                                py: 1.25,
                                            }}
                                        >
                                            <Typography variant="body2">No artwork targets were found for this document yet.</Typography>
                                        </Box>
                                    ) : null}

                                    <Stack spacing={2}>
                                        {rows.map((entry, index) => {
                                            const mapping = createRowMap(mapperData).get(entry.key) || null;
                                            const status = normalizeStatus(mapping?.proofRow || null);
                                            const displayPath = getDisplayFilePath(entry.values);
                                            return (
                                                <Card key={`${entry.key}-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                                                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                    {getArtworkLabel(entry.artwork, index)}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                                    {getArtworkSecondaryLabel(entry.artwork) || "Artwork target details unavailable."}
                                                                </Typography>
                                                            </Box>
                                                            <Chip size="small" color={status.tone} label={status.label} />
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                borderRadius: 2,
                                                                border: "1px solid var(--ec-color-border)",
                                                                backgroundColor: "var(--ec-color-surface-muted)",
                                                                px: 1.5,
                                                                py: 1.25,
                                                            }}
                                                        >
                                                            {(() => {
                                                                const previewImagePath = getPreviewImagePath(entry.values);
                                                                return (
                                                                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                                                                        {previewImagePath ? (
                                                                            <Box
                                                                                component="img"
                                                                                src={previewImagePath}
                                                                                alt={getDisplayFileName(entry.values)}
                                                                                sx={{
                                                                                    width: 72,
                                                                                    height: 72,
                                                                                    objectFit: "cover",
                                                                                    borderRadius: 1.5,
                                                                                    border: "1px solid",
                                                                                    borderColor: "divider",
                                                                                    backgroundColor: "background.paper",
                                                                                    flexShrink: 0,
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.35 }}>
                                                                                Filename
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                                                                                {getDisplayFileName(entry.values)}
                                                                            </Typography>
                                                                            {displayPath ? (
                                                                                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                                                                                    File selected and ready to preview.
                                                                                </Typography>
                                                                            ) : null}
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })()}
                                                        </Box>

                                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                            {displayPath ? (
                                                                <Button href={displayPath} target="_blank" rel="noopener noreferrer">
                                                                    Preview
                                                                </Button>
                                                            ) : null}
                                                            <Button
                                                                variant="outlined"
                                                                startIcon={<FolderOpenIcon />}
                                                                onClick={() => setFilePickerTarget({ type: "row", key: entry.key })}
                                                                disabled={disabled || saving || customerFilesLoading || !trimmedCustomerId || editingLocked}
                                                            >
                                                                {customerFilesLoading
                                                                    ? "Loading files..."
                                                                    : displayPath
                                                                        ? "Replace / Select Internal File"
                                                                        : "Select Internal File"}
                                                            </Button>
                                                        </Box>

                                                        <Box
                                                            component="details"
                                                            sx={{
                                                                "& > summary": {
                                                                    cursor: "pointer",
                                                                    color: "text.secondary",
                                                                    fontSize: "0.8rem",
                                                                    fontWeight: 700,
                                                                },
                                                            }}
                                                        >
                                                            <summary>File details / advanced</summary>
                                                            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                                                                <TextField
                                                                    label="Proof File Name"
                                                                    size="small"
                                                                    value={entry.values.sourceFileName}
                                                                    disabled={editingLocked}
                                                                    onChange={(event) =>
                                                                        setRowsByKey((prev) => ({
                                                                            ...prev,
                                                                            [entry.key]: {
                                                                                ...prev[entry.key],
                                                                                sourceFileName: event.target.value,
                                                                                sourceFileId: buildFallbackSourceFileId({
                                                                                    ...prev[entry.key],
                                                                                    sourceFileName: event.target.value,
                                                                                }),
                                                                            },
                                                                        }))
                                                                    }
                                                                />
                                                                <TextField
                                                                    label="Proof File URL or Path"
                                                                    size="small"
                                                                    value={entry.values.sourceFilePath}
                                                                    disabled={editingLocked}
                                                                    onChange={(event) =>
                                                                        setRowsByKey((prev) => ({
                                                                            ...prev,
                                                                            [entry.key]: {
                                                                                ...prev[entry.key],
                                                                                sourceFilePath: event.target.value,
                                                                                sourceFileId: buildFallbackSourceFileId({
                                                                                    ...prev[entry.key],
                                                                                    sourceFilePath: event.target.value,
                                                                                }),
                                                                            },
                                                                        }))
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    {!packageFiles.length ? (
                                        <Box
                                            sx={{
                                                borderRadius: 2,
                                                border: "1px solid var(--ec-color-border)",
                                                backgroundColor: "var(--ec-color-surface-muted)",
                                                px: 1.5,
                                                py: 1.25,
                                            }}
                                        >
                                            <Typography variant="body2">No package files added yet. Add one or more files to build the proof package.</Typography>
                                        </Box>
                                    ) : null}

                                    <Stack spacing={2}>
                                        {packageFiles.map((entry, index) => {
                                            const status = normalizeStatus(entry.proofRow || null);
                                            const selectedProducts = resolveSelectedProductOptions(entry, productOptions);
                                            const selectedDecorations = resolveSelectedDecorationOptions(entry, decorationOptions);
                                            const missingProducts = countMissingProductTargets(entry, productOptions);
                                            const missingDecorations = countMissingDecorationTargets(entry, decorationOptions);
                                            const legacyPoGroupTargeting = isLegacyPoGroupTargeting(entry);
                                            const legacyOrderTargeting = isLegacyOrderTargeting(entry);
                                            const appliesToScope = [APPLIES_TO_ORDER, APPLIES_TO_PRODUCTS, APPLIES_TO_DECORATIONS].includes(entry?.appliesTo?.scope)
                                                ? entry.appliesTo.scope
                                                : APPLIES_TO_PRODUCTS;
                                            const displayPath = getDisplayFilePath(entry);
                                            return (
                                                <Card key={`${entry.mappingId || "new"}-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                                                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                    Proof file {index + 1}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                                    Add the customer-facing file first, then choose what it applies to.
                                                                </Typography>
                                                            </Box>
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip size="small" color={status.tone} label={status.label} />
                                                                <Button
                                                                    color="error"
                                                                    startIcon={<DeleteOutlineIcon />}
                                                                    onClick={() =>
                                                                        setPackageState((prev) => ({
                                                                            ...prev,
                                                                            packageFiles: prev.packageFiles.filter((_, fileIndex) => fileIndex !== index),
                                                                        }))
                                                                    }
                                                                    disabled={editingLocked}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </Stack>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                borderRadius: 2,
                                                                border: "1px solid var(--ec-color-border)",
                                                                backgroundColor: "var(--ec-color-surface-muted)",
                                                                px: 1.5,
                                                                py: 1.25,
                                                            }}
                                                        >
                                                            {(() => {
                                                                const previewImagePath = getPreviewImagePath(entry);
                                                                return (
                                                                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                                                                        {previewImagePath ? (
                                                                            <Box
                                                                                component="img"
                                                                                src={previewImagePath}
                                                                                alt={getDisplayFileName(entry)}
                                                                                sx={{
                                                                                    width: 72,
                                                                                    height: 72,
                                                                                    objectFit: "cover",
                                                                                    borderRadius: 1.5,
                                                                                    border: "1px solid",
                                                                                    borderColor: "divider",
                                                                                    backgroundColor: "background.paper",
                                                                                    flexShrink: 0,
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.35 }}>
                                                                                Filename
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                                                                                {getDisplayFileName(entry)}
                                                                            </Typography>
                                                                            {displayPath ? (
                                                                                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                                                                                    File selected and ready to preview.
                                                                                </Typography>
                                                                            ) : null}
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })()}
                                                        </Box>

                                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                            {displayPath ? (
                                                                <Button href={displayPath} target="_blank" rel="noopener noreferrer">
                                                                    Preview
                                                                </Button>
                                                            ) : null}
                                                            <Button
                                                                variant="outlined"
                                                                startIcon={<FolderOpenIcon />}
                                                                onClick={() => setFilePickerTarget({ type: "package", index })}
                                                                disabled={disabled || saving || customerFilesLoading || !trimmedCustomerId || editingLocked}
                                                            >
                                                                {customerFilesLoading
                                                                    ? "Loading files..."
                                                                    : displayPath
                                                                        ? "Replace / Select Internal File"
                                                                        : "Select Internal File"}
                                                            </Button>
                                                        </Box>

                                                        <Box
                                                            component="details"
                                                            sx={{
                                                                "& > summary": {
                                                                    cursor: "pointer",
                                                                    color: "text.secondary",
                                                                    fontSize: "0.8rem",
                                                                    fontWeight: 700,
                                                                },
                                                            }}
                                                        >
                                                            <summary>File details / advanced</summary>
                                                            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                                                                <TextField
                                                                    label="Proof File Name"
                                                                    size="small"
                                                                    value={entry.sourceFileName}
                                                                    disabled={editingLocked}
                                                                    onChange={(event) =>
                                                                        updatePackageEntry(index, (current) => {
                                                                            const next = { ...current, sourceFileName: event.target.value };
                                                                            next.sourceFileId = buildFallbackSourceFileId(next);
                                                                            return next;
                                                                        })
                                                                    }
                                                                />
                                                                <TextField
                                                                    label="Proof File URL or Path"
                                                                    size="small"
                                                                    value={entry.sourceFilePath}
                                                                    disabled={editingLocked}
                                                                    onChange={(event) =>
                                                                        updatePackageEntry(index, (current) => {
                                                                            const next = { ...current, sourceFilePath: event.target.value };
                                                                            next.sourceFileId = buildFallbackSourceFileId(next);
                                                                            return next;
                                                                        })
                                                                    }
                                                                />
                                                                {entry.mappingId ? (
                                                                    <Box>
                                                                        <Button onClick={() => handleUpdatePackageMapping(entry)} disabled={editingLocked}>
                                                                            Save Assignment Only
                                                                        </Button>
                                                                    </Box>
                                                                ) : null}
                                                            </Stack>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                borderTop: "1px solid",
                                                                borderColor: "divider",
                                                                pt: 1.5,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: 1.25,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                                                                    Step 3
                                                                </Typography>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                    What is this proof for?
                                                                </Typography>
                                                            </Box>

                                                            {legacyPoGroupTargeting ? (
                                                                <Box
                                                                    sx={{
                                                                        borderRadius: 2,
                                                                        border: "1px solid var(--ec-status-warning-border)",
                                                                        backgroundColor: "var(--ec-status-warning-bg)",
                                                                        px: 1.25,
                                                                        py: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ color: "var(--ec-status-warning-text)" }}>
                                                                        This package file has legacy PO-group targeting. Re-save it using a clearer product or decoration/location target.
                                                                    </Typography>
                                                                </Box>
                                                            ) : null}
                                                            {legacyOrderTargeting ? (
                                                                <Box
                                                                    sx={{
                                                                        borderRadius: 2,
                                                                        border: "1px solid var(--ec-status-warning-border)",
                                                                        backgroundColor: "var(--ec-status-warning-bg)",
                                                                        px: 1.25,
                                                                        py: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ color: "var(--ec-status-warning-text)" }}>
                                                                        Previously saved for the entire order. Choose a product or location if you want more precise downstream reuse.
                                                                    </Typography>
                                                                </Box>
                                                            ) : null}

                                                            <Box
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                                                                    gap: 1.25,
                                                                }}
                                                            >
                                                                {[
                                                                    {
                                                                        value: APPLIES_TO_ORDER,
                                                                        label: "Entire order",
                                                                        description: "Use this when the same proof applies to the full sales order.",
                                                                    },
                                                                    {
                                                                        value: APPLIES_TO_PRODUCTS,
                                                                        label: "Specific product",
                                                                        description: "Choose one or more products that should use this proof.",
                                                                    },
                                                                    {
                                                                        value: APPLIES_TO_DECORATIONS,
                                                                        label: "Specific decoration/location",
                                                                        description: "Choose the exact decoration or artwork location this proof belongs to.",
                                                                    },
                                                                ].map((option) => {
                                                                    const selected = appliesToScope === option.value;
                                                                    return (
                                                                        <Box
                                                                            key={option.value}
                                                                            component="button"
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const nextSelection = createScopeSelectionEntry(option.value);
                                                                                updatePackageEntry(index, (current) => ({
                                                                                    ...current,
                                                                                    ...nextSelection,
                                                                                }));
                                                                            }}
                                                                            disabled={editingLocked}
                                                                            sx={{
                                                                                textAlign: "left",
                                                                                p: 1.25,
                                                                                borderRadius: 2,
                                                                                border: "1px solid",
                                                                                borderColor: selected ? "primary.main" : "divider",
                                                                                backgroundColor: selected ? "var(--ec-color-primary-soft)" : "background.paper",
                                                                                boxShadow: selected ? "0 0 0 1px var(--ec-color-primary)" : "none",
                                                                                cursor: editingLocked ? "not-allowed" : "pointer",
                                                                                color: "text.primary",
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
                                                                                {option.label}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                                {option.description}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>

                                                            {appliesToScope === APPLIES_TO_ORDER ? (
                                                                <Box
                                                                    sx={{
                                                                        borderRadius: 2,
                                                                        border: "1px solid var(--ec-color-border)",
                                                                        backgroundColor: "var(--ec-color-surface-muted)",
                                                                        px: 1.5,
                                                                        py: 1.25,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2">
                                                                        This proof stays available to the full sales order. Use a narrower target only when the proof belongs to a specific product or decoration.
                                                                    </Typography>
                                                                </Box>
                                                            ) : null}

                                                            {appliesToScope === APPLIES_TO_PRODUCTS ? (
                                                                <>
                                                                    <Autocomplete
                                                                        multiple
                                                                        options={productOptions}
                                                                        value={selectedProducts}
                                                                        getOptionLabel={buildProductOptionLabel}
                                                                        isOptionEqualToValue={(option, value) => option.orderItemId === value.orderItemId}
                                                                        disabled={editingLocked}
                                                                        onChange={(_, nextOptions) => {
                                                                            updatePackageEntry(index, (current) => ({
                                                                                ...current,
                                                                                proofScope: "selected_targets",
                                                                                appliesTo: {
                                                                                    ...emptyAppliesTo(),
                                                                                    scope: APPLIES_TO_PRODUCTS,
                                                                                    orderItemIds: nextOptions.map((option) => option.orderItemId),
                                                                                },
                                                                            }));
                                                                        }}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Products"
                                                                                placeholder="Select one or more products"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                    />
                                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                        Supplier/PO targeting will be derived from the selected product data.
                                                                    </Typography>
                                                                    {missingProducts > 0 ? (
                                                                        <Chip
                                                                            size="small"
                                                                            color="warning"
                                                                            label={`${missingProducts} saved product target${missingProducts === 1 ? " is" : "s are"} no longer available on this order.`}
                                                                            sx={{ width: "fit-content" }}
                                                                        />
                                                                    ) : null}
                                                                </>
                                                            ) : null}

                                                            {appliesToScope === APPLIES_TO_DECORATIONS ? (
                                                                <>
                                                                    <Autocomplete
                                                                        multiple
                                                                        options={decorationOptions}
                                                                        value={selectedDecorations}
                                                                        getOptionLabel={buildDecorationOptionLabel}
                                                                        isOptionEqualToValue={(option, value) => option.key === value.key}
                                                                        disabled={editingLocked}
                                                                        onChange={(_, nextOptions) => {
                                                                            updatePackageEntry(index, (current) => ({
                                                                                ...current,
                                                                                proofScope: "selected_targets",
                                                                                appliesTo: {
                                                                                    ...emptyAppliesTo(),
                                                                                    scope: APPLIES_TO_DECORATIONS,
                                                                                    orderItemIds: [...new Set(nextOptions.map((option) => option.orderItemId).filter(Boolean))],
                                                                                    decorationIds: [...new Set(nextOptions.map((option) => option.decorationId).filter(Boolean))],
                                                                                    artworkIds: [...new Set(nextOptions.map((option) => option.artworkId).filter(Boolean))],
                                                                                },
                                                                            }));
                                                                        }}
                                                                        renderOption={(props, option) => (
                                                                            <li {...props} key={option.key}>
                                                                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                                                    <Typography variant="body2">{buildDecorationOptionLabel(option)}</Typography>
                                                                                    {option?.imprintType ? (
                                                                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                                            {option.imprintType}
                                                                                        </Typography>
                                                                                    ) : null}
                                                                                </Box>
                                                                            </li>
                                                                        )}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Decoration / Location Targets"
                                                                                placeholder="Select one or more decoration targets"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                    />
                                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                        Supplier/PO targeting will be derived from the selected product and decoration/location data.
                                                                    </Typography>
                                                                    {missingDecorations > 0 ? (
                                                                        <Chip
                                                                            size="small"
                                                                            color="warning"
                                                                            label={`${missingDecorations} saved decoration target${missingDecorations === 1 ? " is" : "s are"} no longer available on this order.`}
                                                                            sx={{ width: "fit-content" }}
                                                                        />
                                                                    ) : null}
                                                                </>
                                                            ) : null}
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </Stack>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Box
                        sx={(theme) => ({
                            position: "sticky",
                            bottom: 0,
                            zIndex: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor: alpha(theme.palette.background.paper, 0.94),
                            backdropFilter: "blur(8px)",
                            px: { xs: 1.25, sm: 1.5 },
                            py: 1.25,
                            borderRadius: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                        })}
                    >
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {mode === COMPLETE_APPROVAL_MODE
                                ? "Save the current proof package and targeting selections."
                                : "Save the current proof assignments for each artwork location."}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={mode === COMPLETE_APPROVAL_MODE ? handleSavePackage : handleSavePerLocation}
                            disabled={interactionDisabled}
                        >
                            {mode === COMPLETE_APPROVAL_MODE ? "Save proof package" : "Save per-location proof assignments"}
                        </Button>
                    </Box>
                </Stack>

            </Box>

            <Dialog open={reopenDialogOpen} onClose={() => setReopenDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Reopen approved proofs?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will allow the approved proof files to be revised. Any revised proof will need to be approved again before the Sales Order can proceed.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                        Continue only if you need to replace or revise an approved proof.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReopenDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            setRevisionUnlocked(true);
                            setReopenDialogOpen(false);
                        }}
                    >
                        Reopen proofing
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(resetDialogState)}
                onClose={() => (resetting ? null : setResetDialogState(null))}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{resetDialogState?.title || "Reset Artwork & Proofing?"}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {resetDialogState?.description ||
                            "This will clear the current proof setup and return Artwork & Proofing to its default state. Existing artwork, proof files, and proof history will be preserved."}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogState(null)} disabled={resetting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleResetArtworkProofing} disabled={resetting}>
                        {resetting ? "Resetting..." : "Reset"}
                    </Button>
                </DialogActions>
            </Dialog>

            <FilesPopup
                open={Boolean(filePickerTarget) && !editingLocked}
                onClose={() => setFilePickerTarget(null)}
                onImageSelect={() => {}}
                onSelectFile={handleSelectInternalFile}
                handleDone={handleCustomerFileUpload}
                imageGroups={customerFileGroups}
                selectedImage={selectedFilePath}
                customerId={trimmedCustomerId || null}
                allowUpload={Boolean(trimmedCustomerId)}
            />
        </Box>
    );
}
